import React, { useState, useEffect, useRef } from 'react';
import { generateResponsiveImageUrls } from '../../utils/imageOptimization';

/**
 * LazyImage Component
 * Implements lazy loading for images with blur-up placeholder effect
 * Uses Intersection Observer API for optimal performance
 * Supports responsive images with srcset for optimal loading
 */
// Helper to fix malformed URLs (missing colon after protocol)
const cleanUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('https//')) {
    return 'https://' + url.substring(7);
  }
  if (url.startsWith('http//')) {
    return 'http://' + url.substring(6);
  }
  return url;
};

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholderSrc = null,
  onLoad = null,
  onError = null,
  useResponsive = true, // Enable responsive image loading
  ...props 
}) => {
  // Clean the source URL in case it has malformed protocol
  const cleanedSrc = cleanUrl(src);
  
  const [imageSrc, setImageSrc] = useState(placeholderSrc || null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: load image immediately
      setImageSrc(cleanedSrc);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Image is in viewport, start loading
            setImageSrc(cleanedSrc);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [cleanedSrc]);

  const handleLoad = (e) => {
    setImageLoaded(true);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    setImageError(true);
    if (onError) onError(e);
  };

  // Generate responsive image URLs
  const { src: responsiveSrc, srcSet, sizes } = useResponsive 
    ? generateResponsiveImageUrls(imageSrc || cleanedSrc)
    : { src: imageSrc || cleanedSrc, srcSet: '', sizes: '' };

  return (
    <div className="relative" ref={imgRef}>
      {imageSrc && (
        <img
          src={responsiveSrc}
          srcSet={srcSet || undefined}
          sizes={sizes || undefined}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy" // Native lazy loading as additional fallback
          {...props}
        />
      )}
      
      {/* Loading placeholder */}
      {!imageLoaded && !imageError && (
        <div 
          className={`absolute inset-0 bg-gray-200 animate-pulse ${className}`}
          style={{ aspectRatio: props.style?.aspectRatio }}
        />
      )}
      
      {/* Error state */}
      {imageError && (
        <div 
          className={`absolute inset-0 bg-gray-100 flex items-center justify-center ${className}`}
        >
          <div className="text-center text-gray-400">
            <svg 
              className="h-12 w-12 mx-auto mb-2" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
              />
            </svg>
            <p className="text-xs">Failed to load image</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;

